// Tab "Attivita'" della vista admin globale (solo super_admin): statistiche
// d'uso dal segnale di presenza (user_activity / user_activity_days).
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Activity, Smartphone, Users } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { supabaseV2 } from "../../lib/supabase";
import { EmptyState, SectionLoading } from "../../components/state";
import { MetricCard } from "../../components/primitives";
import { AreaChart } from "../../components/charts";

type Stats = {
  summary: {
    active_today: number;
    active_7d: number;
    active_30d: number;
    any_signal_7d: number;
    any_signal_30d: number;
    users_total: number;
    users_with_team: number;
    tracking_since: string | null;
  };
  daily: Array<{ day: string; active: number; signups: number }>;
  platforms: Record<string, number>;
  teams: Array<{
    team_id: string;
    team_name: string;
    members: number;
    active_7d: number;
    active_30d: number;
    last_active_at: string | null;
  }>;
  recent: Array<{
    user_id: string;
    email: string;
    full_name: string;
    last_seen_at: string;
    platform: string | null;
    touches: number;
    team_name: string | null;
    days_active_30d: number;
  }>;
  hours: Array<{ hour: number; n: number }>;
};

const PERIODS = [14, 30, 90] as const;

function timeAgo(iso: string, t: (k: string, o?: any) => string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60_000);
  if (m < 60) return t("admin.activity.ago.minutes", { count: m });
  const h = Math.floor(m / 60);
  if (h < 48) return t("admin.activity.ago.hours", { count: h });
  return t("admin.activity.ago.days", { count: Math.floor(h / 24) });
}

export function ActivityAdminSection({ intlLocale }: { intlLocale: string }) {
  const { t } = useTranslation("settings");
  const [days, setDays] = useState<(typeof PERIODS)[number]>(30);

  const q = useQuery({
    queryKey: ["admin-activity-stats", days],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("admin_activity_stats", { p_days: days });
      if (error) throw error;
      return data as Stats;
    },
    staleTime: 60_000,
  });

  if (q.isLoading) return <SectionLoading />;
  if (q.error) return <p className="text-sm text-rose-600">{(q.error as any)?.message || String(q.error)}</p>;
  const s = q.data!;
  const sum = s.summary;
  const fmtDay = (d: string) => new Date(d).toLocaleDateString(intlLocale, { day: "numeric", month: "short" });
  const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleString(intlLocale, { dateStyle: "short", timeStyle: "short" }) : "—");
  const platformLabel = (p: string | null) =>
    p === "pwa" ? t("admin.activity.platform.pwa") : p === "web" ? t("admin.activity.platform.web") : p === "backfill" ? t("admin.activity.platform.backfill") : t("admin.activity.platform.unknown");
  const pwa = s.platforms.pwa || 0;
  const web = s.platforms.web || 0;
  const backfillOnly = s.platforms.backfill || 0;
  const hoursMax = Math.max(1, ...s.hours.map((h) => h.n));

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label={t("admin.activity.kpi.today")}
          value={sum.active_today}
          subtitle={t("admin.activity.kpi.ofUsers", { count: sum.users_with_team })}
          icon={Activity}
          gradient="gradient-card-blue"
        />
        <MetricCard
          label={t("admin.activity.kpi.week")}
          value={sum.active_7d}
          subtitle={t("admin.activity.kpi.anySignal", { count: sum.any_signal_7d })}
          icon={Users}
        />
        <MetricCard
          label={t("admin.activity.kpi.month")}
          value={sum.active_30d}
          subtitle={t("admin.activity.kpi.anySignal", { count: sum.any_signal_30d })}
          icon={Users}
        />
        <MetricCard
          label={t("admin.activity.kpi.installed")}
          value={pwa}
          subtitle={t("admin.activity.kpi.installedDesc", { web, pending: backfillOnly })}
          icon={Smartphone}
        />
      </div>
      {sum.tracking_since && (
        <p className="text-xs text-muted-foreground">
          {t("admin.activity.trackingSince", { date: fmtDay(sum.tracking_since) })}
        </p>
      )}

      <Card>
        <CardHeader className="pb-3 flex flex-row flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="text-lg">{t("admin.activity.daily.title")}</CardTitle>
            <CardDescription>{t("admin.activity.daily.desc")}</CardDescription>
          </div>
          <div className="inline-flex rounded-2xl bg-background p-0.5 text-sm">
            {PERIODS.map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={cn(
                  "px-2.5 py-1 rounded font-medium transition-colors",
                  days === d ? "bg-card text-foreground " : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t("admin.activity.days", { count: d })}
              </button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {s.daily.every((d) => d.active === 0) ? (
            <EmptyState text={t("admin.activity.daily.empty")} />
          ) : (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">{t("admin.activity.daily.active")}</p>
              <AreaChart values={s.daily.map((d) => d.active)} labels={s.daily.map((d) => fmtDay(d.day))} height={160} />
            </div>
          )}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">{t("admin.activity.daily.signups")}</p>
            <AreaChart
              values={s.daily.map((d) => d.signups)}
              labels={s.daily.map((d) => fmtDay(d.day))}
              color="hsl(var(--primary))"
              height={100}
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("admin.activity.hours.title")}</CardTitle>
            <CardDescription>{t("admin.activity.hours.desc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-[3px] h-28">
              {s.hours.map((h) => (
                <div key={h.hour} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0" title={`${h.hour}:00 · ${h.n}`}>
                  <div
                    className="w-full rounded-sm bg-[hsl(var(--accent))]"
                    style={{ height: `${Math.max(2, (h.n / hoursMax) * 100)}%`, opacity: h.n ? 1 : 0.25 }}
                  />
                  {h.hour % 6 === 0 && <span className="text-[11px] text-muted-foreground">{h.hour}</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t("admin.activity.teams.title")}</CardTitle>
            <CardDescription>{t("admin.activity.teams.desc")}</CardDescription>
          </CardHeader>
          <CardContent>
            {s.teams.length === 0 ? (
              <EmptyState text={t("admin.activity.teams.empty")} />
            ) : (
              <div className="overflow-x-auto max-h-80 overflow-y-auto">
                <table className="w-full text-[12.5px]">
                  <thead className="text-muted-foreground sticky top-0 bg-card">
                    <tr>
                      <th className="text-left font-mono uppercase text-xs tracking-wider py-1 pr-2">{t("admin.activity.teams.col.team")}</th>
                      <th className="text-right font-mono uppercase text-xs tracking-wider px-1">7g</th>
                      <th className="text-right font-mono uppercase text-xs tracking-wider px-1">30g</th>
                      <th className="text-right font-mono uppercase text-xs tracking-wider px-1">{t("admin.activity.teams.col.members")}</th>
                      <th className="text-right font-mono uppercase text-xs tracking-wider pl-1">{t("admin.activity.teams.col.last")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.teams.map((tm) => (
                      <tr key={tm.team_id} className="border-t">
                        <td className="py-1.5 pr-2 font-medium truncate max-w-[180px]">{tm.team_name}</td>
                        <td className="text-right font-tabular px-1">{tm.active_7d}</td>
                        <td className="text-right font-tabular px-1">{tm.active_30d}</td>
                        <td className="text-right font-tabular px-1 text-muted-foreground">{tm.members}</td>
                        <td className="text-right whitespace-nowrap pl-1 text-muted-foreground">
                          {tm.last_active_at ? timeAgo(tm.last_active_at, t) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t("admin.activity.recent.title")}</CardTitle>
          <CardDescription>{t("admin.activity.recent.desc")}</CardDescription>
        </CardHeader>
        <CardContent>
          {s.recent.length === 0 ? (
            <EmptyState text={t("admin.activity.recent.empty")} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left font-mono uppercase text-xs tracking-wider py-1 pr-2">{t("admin.activity.recent.col.user")}</th>
                    <th className="text-left font-mono uppercase text-xs tracking-wider px-1">{t("admin.activity.recent.col.team")}</th>
                    <th className="text-left font-mono uppercase text-xs tracking-wider px-1">{t("admin.activity.recent.col.platform")}</th>
                    <th className="text-right font-mono uppercase text-xs tracking-wider px-1">{t("admin.activity.recent.col.days")}</th>
                    <th className="text-right font-mono uppercase text-xs tracking-wider pl-1">{t("admin.activity.recent.col.last")}</th>
                  </tr>
                </thead>
                <tbody>
                  {s.recent.map((u) => (
                    <tr key={u.user_id} className="border-t">
                      <td className="py-1.5 pr-2">
                        <span className="font-medium">{u.full_name}</span>
                        <span className="block text-xs text-muted-foreground">{u.email}</span>
                      </td>
                      <td className="px-1 text-muted-foreground truncate max-w-[160px]">{u.team_name || "—"}</td>
                      <td className="px-1">{platformLabel(u.platform)}</td>
                      <td className="text-right font-tabular px-1">{u.days_active_30d}</td>
                      <td className="text-right whitespace-nowrap pl-1" title={fmtDate(u.last_seen_at)}>{timeAgo(u.last_seen_at, t)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
